<template>
  <div v-if="isSample" class="workflow-status-item sample">
    <i class="ddi ddi-navigation-menu hidden" />
    <div class="workflow-status-item__item-editor">
      <input class="bluish-grey no-border" maxlength="100">
      <workflow-status-color-picker color="blue" />
      <div class="active-toggle">
        <chicklet
          :color="status.isInactive ? 'bluish-grey' : 'grey'" 
          size="tiny" 
          is-uppercase 
          @click="toggleActiveState"><span style="opacity:0;">Active</span></chicklet>
      </div>
    </div>
  </div>
  <div v-else :class="{'read-only': readOnly}" class="workflow-status-item">
    <i :class="{'hidden': !showDrag || readOnly}" class="ddi ddi-navigation-menu handle"/>
    <div class="workflow-status-item__item-editor">
      <input ref="nameInput" v-model="status.name" :class="status.color" :tabindex="index+1" placeholder="Name status..." maxlength="100">
      <workflow-status-color-picker v-model:color="status.color" />
      <div class="active-toggle">
        <chicklet
          :color="status.isInactive ? 'bluish-grey' : 'grey'" 
          size="tiny" 
          is-uppercase 
          @click="toggleActiveState">{{ activeLabel }}</chicklet>
        <el-tooltip v-if="status.isInactive" placement="top">
          <i class="ddi ddi-info" />
          <template #content>
            <div>
              A {{ TextAssistant.itemSingularName(entityType).toLowerCase() }} with this status is considered <strong>completed</strong> 
              or <strong>on hold</strong>. Inactive {{ TextAssistant.itemPluralName(entityType).toLowerCase() }} only appear under the 
              "Inactive" and "Everything" filters.
            </div>
          </template>
        </el-tooltip>
      </div>
    </div>
    <div v-if="allowJobsManagement" class="workflow-status-item__assignee-mandate">
      <!-- At this point, we can assume status is IWorkflowsInputModifyStatusesStatus -->
      <workflow-job-picker 
        v-model:selected-job-id="status.assigneeMandateJobID"
        :jobs="jobs" 
      />
    </div>
    <div class="workflow-status-item__options">
      <more-button-dropdown v-if="showMoreIcon(status)">
        <template #options-list>
          <ul>
            <li v-if="showStatusTransitionsIcon(status)" @click="$emit('settings-clicked', status.id)" >
              <i class="ddi ddi-cog" />
              <span>Status transitions</span>
            </li>
            <li v-if="showDelete" @click="handleDeleteClick(index)">
              <i class="ddi ddi-trash" />
              <span>Delete</span>
            </li>
          </ul>
        </template>
      </more-button-dropdown>
      <template v-else>
        <i v-if="showStatusTransitionsIcon(status)" class="ddi ddi-cog" title="Status transitions" @click="$emit('settings-clicked', status.id)" />
        <i v-if="showDelete" class="ddi ddi-trash" @click="handleDeleteClick(index)" />
      </template>
    </div>
  </div>
</template>

<script lang="ts">
/* General */
import { defineComponent } from 'vue'

/* Mixins */

/* Components */

/* Helpers */
import ColorAssistant from '@/helpers/color-assistant'

/* Data types */
import {
  IWorkflowsInputCreateStatus,
  IWorkflowsInputModifyJobsJob,
  IWorkflowsInputModifyStatusesStatus,
} from '@/api/types/request.types'
import { EEntities } from '@/types'
import TextAssistant from '@/helpers/text-assistant'

/* API */

export default defineComponent({
  name: 'WorkflowStatusItem',

  props: {
    index: {
      type: Number,
      default: -1,
    },
    status: {
      type: Object as () =>
        | (IWorkflowsInputCreateStatus & Partial<IWorkflowsInputModifyStatusesStatus>)
        | IWorkflowsInputModifyStatusesStatus,
      required: true,
    },
    showDelete: {
      type: Boolean,
      default: false,
    },
    showDrag: {
      type: Boolean,
      default: false,
    },
    showSettings: {
      type: Boolean,
      default: false,
    },
    /* Jobs related */
    selectedJobId: {
      type: Number,
      default: null,
    },
    /* Jobs related */
    jobs: {
      type: Array as () => IWorkflowsInputModifyJobsJob[],
      default: null,
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
    isSample: {
      type: Boolean,
      default: false,
    },
    entityType: {
      type: String as () => EEntities,
      required: true,
    },
    focusOnCreate: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['delete-clicked', 'settings-clicked'],

  data() {
    return {
      ColorAssistant,
      TextAssistant,
    }
  },

  computed: {
    activeLabel(): string {
      return this.status.isInactive ? 'Inactive' : 'Active'
    },
    allowJobsManagement(): boolean {
      return (
        this.$permissions.jobsFeatureEnabled &&
        this.jobs !== null &&
        this.jobs.length > 0
      )
    },
  },

  created() {
    if (this.focusOnCreate) {
      this.$nextTick(() => {
        // @ts-ignore
        this.$refs.nameInput.focus()
      })
    }
  },

  methods: {
    showMoreIcon(
      status: IWorkflowsInputCreateStatus | IWorkflowsInputModifyStatusesStatus,
    ): boolean {
      return this.showStatusTransitionsIcon(status) && this.showDelete
    },
    showStatusTransitionsIcon(
      status: IWorkflowsInputCreateStatus | IWorkflowsInputModifyStatusesStatus,
    ): boolean {
      // @ts-ignore
      return this.showSettings && !this.readOnly && status.id
    },
    toggleActiveState() {
      this.status.isInactive = !this.status.isInactive
    },
    handleDeleteClick(index: number) {
      if (!this.showDelete) {
        return
      }
      this.$emit('delete-clicked', index)
    },
  },
})
</script>

<style lang="scss">
.workflow-status-item {
  width: 100%;
  border: $border--dotted-medium;
  margin-bottom: 4px;
  padding: 8px 12px;
  display: flex;
  align-items: center;
  background-color: var(--color-background-primary);
  @include flex-row-horizontal-spacing(8px);
  @include standard-box-shadow;

  &.read-only {
    pointer-events: none;
  }

  &.sample {
    opacity: 0.4;
    * {
      pointer-events: none;
    }

    .workflow-status-color-picker > div {
      background-color: $color--gray-60 !important;
    }
  }

  .ddi {
    cursor: pointer;
    font-size: 18px;
    color: var(--color-text-primary);

    &.hidden {
      opacity: 0;
      cursor: default;
    }
  }

  > .ddi-navigation-menu {
    @include clickable-gray-color-states;
    cursor: grab;
  }

  &__item-editor {
    display: flex;
    align-items: center;
    flex-grow: 1;
    @include flex-row-horizontal-spacing(8px);

    input {
      max-width: 190px;
      flex-grow: 1;
      border-radius: 6px;
      outline: none;
      border: $border--solid-medium;
      @include lettering--p-sm;
      font-weight: $bold;
      padding: 4px 6px;

      $input-bg-opacity: 0.24;

      &.dodger-blue {
        border-color: $dodger-blue;
        background-color: rgba($dodger-blue, $input-bg-opacity);
      }

      &.viridian {
        border-color: $viridian;
        background-color: rgba($viridian, $input-bg-opacity);
      }

      &.algae-green {
        border-color: $algae-green;
        background-color: rgba($algae-green, $input-bg-opacity);
      }

      &.bluish-grey {
        border-color: $bluish-grey;
        background-color: rgba($bluish-grey, $input-bg-opacity);

        &.no-border {
          border: none;
        }
      }

      &.bluey-purple {
        border-color: $bluey-purple;
        background-color: rgba($bluey-purple, $input-bg-opacity);
      }

      &.strawberry {
        border-color: $strawberry;
        background-color: rgba($strawberry, $input-bg-opacity);
      }

      &.darkish-pink {
        border-color: $darkish-pink;
        background-color: rgba($darkish-pink, $input-bg-opacity);
      }

      &.reddish-orange {
        border-color: $reddish-orange;
        background-color: rgba($reddish-orange, $input-bg-opacity);
      }

      &.light-orange {
        border-color: $light-orange;
        background-color: rgba($light-orange, $input-bg-opacity);
      }

      @include media-breakpoint-down(sm) {
        width: 100%;
      }
    }
  }

  .active-toggle {
    display: flex;
    align-items: center;
    @include flex-row-horizontal-spacing(4px);

    > .ddi {
      font-size: 14px;
    }
    > .chicklet {
      cursor: pointer;
    }
  }

  &__assignee-mandate {
    @include lettering--p-sm;
  }

  &__options {
    margin-left: auto;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    @include flex-row-horizontal-spacing(10px);

    > .ddi {
      @include clickable-gray-color-states;

      /* Align with more button dropdown icon when seen in a list */
      &.ddi-trash,
      &.ddi-cog {
        margin-left: 14px;
      }
    }
  }
}
</style>
